import { defineComponent } from '@nuxtjs/composition-api';
import UpdateClientForPublicPortfolioCurrentUserMutation from '@/components/partials/portfolio/PortfolioClientMixin/UpdateClientForPublicPortfolioCurrentUser.mutation';
import UpdateClientPositionForPublicPortfolioCurrentUserMutation from '@/components/partials/portfolio/PortfolioClientMixin/UpdateClientPositionForPublicPortfolioCurrentUser.mutation';
import DestroyClientForPublicPortfolioCurrentUserMutation from '@/components/partials/portfolio/PortfolioClientMixin/DestroyClientForPublicPortfolioCurrentUser.mutation';
import CreateCustomCompanyMutation from '@/components/partials/portfolio/PortfolioClientMixin/CreateCustomCompany.mutation';
import CreateClientForPublicPortfolioCurrentUserMutation from '@/components/partials/portfolio/PortfolioClientMixin/CreateClientForPublicPortfolioCurrentUser.mutation';
import PortfolioCurrentUserQuery from '@/apollo/queries/Portfolio/PortfolioCurrentUser.query';
import type {
  UpdateClientPositionForPublicPortfolioCurrentUserInput,
  UpdateClientForPublicPortfolioCurrentUserInput,
  CurrentUser,
} from '@/types/graphql';

export default defineComponent({
  name: 'PortfolioClientMixin',
  methods: {
    showErrorNotice(error: Error | unknown) {
      this.$notice.error('Something went wrong: ', error);

      throw error;
    },
    async updateClient(updateClientAttrs: UpdateClientForPublicPortfolioCurrentUserInput): Promise<void> {
      try {
        await this.$apollo.mutate({
          mutation: UpdateClientForPublicPortfolioCurrentUserMutation,
          variables: {
            input: {
              ...updateClientAttrs,
            },
          },
        });
      } catch (e) {
        this.showErrorNotice(e);
        this.$sentryUtils.error(e);
      }
    },
    async updateClientPosition(
      updateClientPositionAttrs: UpdateClientPositionForPublicPortfolioCurrentUserInput,
    ): Promise<void> {
      try {
        await this.$apollo.mutate({
          mutation: UpdateClientPositionForPublicPortfolioCurrentUserMutation,
          variables: {
            input: {
              ...updateClientPositionAttrs,
            },
          },
          update: (store, result) => {
            const query = {
              query: PortfolioCurrentUserQuery,
            };
            const data = store.readQuery<{ currentUser: CurrentUser }>(query);

            if (!data) return;

            data.currentUser = {
              ...(data.currentUser || {}),
              solidClients: [...result.data.updateClientPositionForPublicPortfolioCurrentUser.clients],
            };

            store.writeQuery({ ...query, data });
          },
        });
      } catch (e) {
        this.showErrorNotice(e);
        this.$sentryUtils.error(e);
      }
    },
    async createCompany(name: string): Promise<void> {
      try {
        const {
          data: { createCustomCompany },
        } = await this.$apollo.mutate({
          mutation: CreateCustomCompanyMutation,
          variables: {
            input: {
              name,
            },
          },
        });

        return createCustomCompany;
      } catch (e) {
        this.showErrorNotice(e);
        this.$sentryUtils.error(e);
      }
    },
    async createClient(companyId: string): Promise<void> {
      try {
        await this.$apollo.mutate({
          mutation: CreateClientForPublicPortfolioCurrentUserMutation,
          variables: {
            input: {
              companyId,
              origin: 'seller_portfolio',
            },
          },
          update: (store, result) => {
            const query = {
              query: PortfolioCurrentUserQuery,
            };
            const data = store.readQuery<{ currentUser: CurrentUser }>(query);

            if (!data) return;

            data.currentUser = {
              ...(data.currentUser || {}),
              solidClients: [
                ...(data.currentUser.solidClients || []),
                result.data.createClientForPublicPortfolioCurrentUser,
              ],
            };

            store.writeQuery({ ...query, data });
          },
        });
      } catch (e) {
        this.showErrorNotice(e);
        this.$sentryUtils.error(e);
      }
    },
    async destroyClient(id: string): Promise<void> {
      try {
        await this.$apollo.mutate({
          mutation: DestroyClientForPublicPortfolioCurrentUserMutation,
          variables: {
            input: {
              id,
            },
          },
          update: store => {
            const query = {
              query: PortfolioCurrentUserQuery,
            };
            const data = store.readQuery<{ currentUser: CurrentUser }>(query);

            if (!data) return;

            const solidClients = (data.currentUser.solidClients || []).filter(client => client.id !== id);

            data.currentUser = {
              ...(data.currentUser || {}),
              solidClients,
            };

            store.writeQuery({ ...query, data });
          },
        });
      } catch (e) {
        this.showErrorNotice(e);
        this.$sentryUtils.error(e);
      }
    },
  },
});
